import { CrudEntity, CrudService, EntityProps } from '@awp/shared/CrudService';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select, { Props as SelectProps, StylesConfig } from 'react-select';
import { CellProps, CodeTableRow, ViewerCellProps } from '../Interfaces/CodeTableInterfaces';
import { sortMixedNumericAlpha } from '@awp/helpers/sorter';

export function AbsoluteSelect<T>(props: SelectProps<T>) {
  const selectRef = useRef<any>(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (menuIsOpen) {
        setMenuIsOpen(false);
      }
    };

    if (menuIsOpen) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [menuIsOpen]);

  const customStyles: StylesConfig<T, boolean> = {
    ...props.styles,
    menuPortal: (base: React.CSSProperties) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  return (
    <Select
      {...props}
      ref={selectRef}
      styles={customStyles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuIsOpen={menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
    />
  );
}
export default class CodeTabelService extends CrudService {
  constructor(crudEntity?: CrudEntity) {
    super(crudEntity);
  }

  override getColumns(props: {
    mode: 'editor' | 'viewer';
    updateRow?: any;
    handleAddRow?: any;
    handleRemoveRow?: any;
    errors?: Record<string, string>;
    isAvalancheObs?: boolean;
  }): any {
    if (props.mode === 'editor') {
      const { updateRow, handleAddRow, handleRemoveRow, errors, isAvalancheObs } = props;

      return [
        {
          Header: 'Value',
          accessor: 'ObservedCharacterValue',
          style: { width: '10%' },
          sortType: sortMixedNumericAlpha,
          Cell: ({ row }: CellProps<CodeTableRow>) => {
            const rowData = row.original;
            const [input, setInput] = React.useState(rowData.ObservedCharacterValue || '');

            React.useEffect(() => {
              setInput(rowData.ObservedCharacterValue || '');
            }, [rowData.ObservedCharacterValue]);

            const handleBlur = () => updateRow?.(rowData.Id, 'ObservedCharacterValue', input);

            return row.original.Id.toString().startsWith('temp-') ? (
              <Form.Control
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onBlur={handleBlur}
                maxLength={100}
                isInvalid={!!errors?.[`value-${rowData.Id}`]}
                className="keep-regular-font"
              />
            ) : (
              <span>{input}</span>
            );
          },
        },
        {
          Header: 'Meaning',
          accessor: 'DisplayedMeaning',
          style: { width: '15%' },
          Cell: ({ row }: CellProps<CodeTableRow>) => {
            const rowData = row.original;
            const [input, setInput] = React.useState(rowData.DisplayedMeaning || '');

            React.useEffect(() => {
              setInput(rowData.DisplayedMeaning || '');
            }, [rowData.DisplayedMeaning]);

            const handleBlur = () => updateRow?.(rowData.Id, 'DisplayedMeaning', input);

            return (
              <Form.Control
                as="textarea"
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onBlur={handleBlur}
                maxLength={2000}
                isInvalid={!!errors?.[`meaning-${rowData.Id}`]}
                className="keep-regular-font"
              />
            );
          },
        },
        {
          Header: 'Hint',
          accessor: 'DisplayedHint',
          style: { width: '35%' },
          Cell: ({ row }: CellProps<CodeTableRow>) => {
            const rowData = row.original;
            const [input, setInput] = React.useState(rowData.DisplayedHint || '');

            React.useEffect(() => {
              setInput(rowData.DisplayedHint || '');
            }, [rowData.DisplayedHint]);

            const handleBlur = () => updateRow?.(rowData.Id, 'DisplayedHint', input);

            return (
              <Form.Control
                as="textarea"
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onBlur={handleBlur}
                maxLength={2000}
                isInvalid={!!errors?.[`hint-${rowData.Id}`]}
                className="keep-regular-font"
              />
            );
          },
        },
        {
          Header: 'Status',
          accessor: 'EndTimestamp',
          style: { width: '20%' },
          Cell: ({ row }: CellProps<CodeTableRow>) => {
            const rowData = row.original;
            const options = [
              { value: true, label: 'Inactive' },
              { value: false, label: 'Active' },
            ];
            return (
              <AbsoluteSelect
                options={options}
                value={rowData.EndTimestamp == null ? options[1] : options[0]}
                onChange={(opt: { value: boolean; label: string } | null) =>
                  updateRow?.(rowData.Id, 'EndTimestamp', opt?.value)
                }
              />
            );
          },
        },
        {
          Header: 'Display Order',
          accessor: 'DisplayOrder',
          style: { width: '10%' },
          sortType: sortMixedNumericAlpha,
          Cell: ({ row }: CellProps<CodeTableRow>) => {
            const rowData = row.original;
            const [input, setInput] = React.useState(rowData.DisplayOrder ?? '');

            React.useEffect(() => {
              setInput(rowData.DisplayOrder ?? '');
            }, [rowData.DisplayOrder]);

            const handleBlur = () => {
              const val = input === '' ? null : parseInt(input as string, 10);
              updateRow?.(rowData.Id, 'DisplayOrder', val);
            };

            return (
              <Form.Control
                type="number"
                min={0}
                max={255}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onBlur={handleBlur}
                isInvalid={!!errors?.[`displayOrder-${rowData.Id}`]}
                className="keep-regular-font"
              />
            );
          },
        },
        {
          Header: '',
          accessor: 'actions',
          style: { width: '5%' },
          disableSortBy: true,
          Cell: ({ row }: CellProps<CodeTableRow>) => (
            <div className="d-flex gap-2 align-items-center">
              <span
                title="Add Row Below"
                onClick={() => handleAddRow?.()}
                className={Object.keys(errors ?? {}).length > 0 ? 'text-muted' : 'text-dark'}
                style={{ cursor: Object.keys(errors ?? {}).length > 0 ? 'not-allowed' : 'pointer' }}
              >
                <i className="fas fa-plus mx-1 my-1 awp-img-btn" />
              </span>
              {(!isAvalancheObs || (isAvalancheObs && row.original.Id.toString().startsWith('temp-'))) && (
                <span
                  onClick={() => handleRemoveRow?.(row.original.Id)}
                  className="text-dark"
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-trash" />
                </span>
              )}
            </div>
          ),
        },
      ];
    }

    // viewer columns
    return [
      {
        Header: 'Value',
        accessor: 'ObservedCharacterValue',
        style: { width: '15%' },
        sortType: sortMixedNumericAlpha,
        Cell: ({ value }: ViewerCellProps) => <span>{value}</span>,
      },
      {
        Header: 'Meaning',
        accessor: 'DisplayedMeaning',
        style: { width: '25%', whiteSpace: 'normal', wordWrap: 'break-word' },
        Cell: ({ value }: ViewerCellProps) => <span>{value}</span>,
      },
      {
        Header: 'Hint',
        accessor: 'DisplayedHint',
        style: { width: '30%', whiteSpace: 'normal', wordWrap: 'break-word' },
        Cell: ({ value }: ViewerCellProps) => <span>{value || ''}</span>,
      },
      {
        Header: 'Status',
        accessor: 'EndTimestamp',
        style: { width: '15%' },
        Cell: ({ value }: ViewerCellProps) => <span>{value ? 'Inactive' : 'Active'}</span>,
      },
      {
        Header: 'Display Order',
        accessor: 'DisplayOrder',
        style: { width: '15%' },
        sortType: sortMixedNumericAlpha,
        Cell: ({ value }: ViewerCellProps) => <span>{value}</span>,
      },
    ];
  }

  override getActionMenu(event: any): React.ReactElement {
    return <></>;
  }

  override getFields(props: any): React.ReactElement {
    return <></>;
  }

  override getValidations(props?: any): any {
    return {};
  }

  override mapInitialValues(props: EntityProps): any {
    return {};
  }

  override mapFormToDTO(props: EntityProps): any {
    return {};
  }

  override cleanFormValues(props: EntityProps): any {
    return {};
  }

  validate(data: any[], isAvlancheObs?: boolean): Record<string, string> {
    const newErrors: Record<string, string> = {};
    const values = new Set<string>();
    const displayOrders = new Set<number>();
    let hasEmptyNew = false;

    data.forEach((row) => {
      const idKey = row.Id;
      const rawValue = row.ObservedCharacterValue?.trim();
      const normalizedValue = rawValue?.toLowerCase();

      if (!rawValue) {
        newErrors[`value-${idKey}`] = 'Value is required.';
      } else if (normalizedValue && values.has(normalizedValue)) {
        newErrors[`value-${idKey}`] = 'Duplicate Value exists. The Values have to be unique within the table';
      } else if (normalizedValue) {
        values.add(normalizedValue);
      }

      if (!isAvlancheObs && !row.DisplayedMeaning?.trim()) {
        newErrors[`meaning-${idKey}`] = 'Meaning is required.';
      }

      if (row.DisplayedHint && row.DisplayedHint.length > 2000) {
        newErrors[`hint-${idKey}`] = 'Hint is too long.';
      }

      if (isAvlancheObs && (row.DisplayOrder === null || row.DisplayOrder === undefined)) {
        newErrors[`displayOrder-${idKey}`] = 'Display Order is required.';
      }

      if (row.DisplayOrder != null) {
        const num = parseInt(row.DisplayOrder, 10);
        if (isNaN(num) || num < 0 || num > 255) {
          newErrors[`displayOrder-${idKey}`] = 'Display Order must be 0–255.';
        } else if (isAvlancheObs && displayOrders.has(num)) {
          newErrors[`displayOrder-${idKey}`] = 'Display Order must be unique.';
        } else {
          displayOrders.add(num);
        }
      }

      const isRowEmpty =
        !row.ObservedCharacterValue &&
        !row.DisplayedMeaning &&
        !row.DisplayedHint &&
        (row.DisplayOrder === null || row.DisplayOrder === undefined);
      if (isRowEmpty) hasEmptyNew = true;
    });

    if (hasEmptyNew) {
      newErrors['row-incomplete'] = 'Complete the empty row first.';
    }

    return newErrors;
  }

  defaultTableProps = {
    fetchData: () => {
      /* Do Nothing */
    },
    loading: false,
    search: '',
    filters: [],
    noRecordsFound: 'No records found',
    defaultSortBy: '',
    defaultDesc: false,
    selectableRows: false,
    dataTriggers: [],
    hiddenColumns: [],
    isFirstColumnSticky: false,
    renderRowSubComponent: null,
    defaultPageSize: 10,
    setDefaultPageSize: () => {
      /* Do Nothing */
    },
    treeExpand: false,
    expendRowStyle: {},
    expendCellStyle: {},
    doubleClickHandler: () => {
      /* Do Nothing */
    },
    mouseDownHandler: () => {
      /* Do Nothing */
    },
    mouseUpHandler: () => {
      /* Do Nothing */
    },
    mouseMoveHandler: () => {
      /* Do Nothing */
    },
    onClickHandler: () => {
      /* Do Nothing */
    },
    onCellStyle: () => ({}),
    index: 0,
    setIndex: () => {
      /* Do Nothing */
    },
    sort: null,
    setSort: () => {
      /* Do Nothing */
    },
    tableParentClasses: undefined,
    tableClasses: undefined,
    paginationBarClasses: undefined,
    trRowStyle: {},
    tdRowStyle: {},
    thRowStyle: {},
  };
}
